import { useEffect, useRef } from "react"
import * as THREE from "three"
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls"
import { useApiService } from "../hooks/Api/useApiService"

export default function Sphere({ onClick, image, size, times }) {
  const mountRef = useRef(null)
  const { loading } = useApiService()

  useEffect(() => {
    const current = mountRef.current
    const scene = new THREE.Scene()
    const camera = new THREE.PerspectiveCamera(70, 1, 0.1, 1000)
    camera.position.z = 5

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
    renderer.setSize(60 * (size || 1), 60 * (size || 1))
    renderer.outputColorSpace = THREE.LinearSRGBColorSpace
    current.appendChild(renderer.domElement)

    const controls = new OrbitControls(camera, renderer.domElement)
    controls.enableDamping = true
    controls.dampingFactor = 0.05
    controls.autoRotate = false
    controls.enableZoom = false

    const geometry = new THREE.SphereGeometry(2, 64, 64)
    const textureLoader = new THREE.TextureLoader()
    const raycaster = new THREE.Raycaster()
    const mouse = new THREE.Vector2()
    let sphere = null
    let material = null
    let loadedTexture = null

    const handleMouseMove = () => {
      renderer.domElement.style.cursor = "pointer"
    }

    const handleClick = (event) => {
      const rect = renderer.domElement.getBoundingClientRect()
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1
      raycaster.setFromCamera(mouse, camera)

      if (sphere) {
        const intersects = raycaster.intersectObject(sphere)
        if (intersects.length > 0 && onClick) {
          onClick()
        }
      }
    }

    renderer.domElement.addEventListener("click", handleClick)
    renderer.domElement.addEventListener("mousemove", handleMouseMove)

    textureLoader.load(process.env.PUBLIC_URL + image, (texture) => {
      loadedTexture = texture
      texture.wrapS = THREE.RepeatWrapping
      texture.repeat.x = times || 2
      texture.colorSpace = THREE.LinearSRGBColorSpace

      material = new THREE.MeshStandardMaterial({
        map: texture,
        roughness: 0.3,
        metalness: 0,
        emissive: new THREE.Color(0x222222),
      })

      sphere = new THREE.Mesh(geometry, material)
      scene.add(sphere)

      const ambientLight = new THREE.AmbientLight(0xffffff, 1.5)
      scene.add(ambientLight)

      const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8)
      directionalLight.position.set(5, 5, 5)
      scene.add(directionalLight)

      let currentSpeed = 0.01
      let targetSpeed = currentSpeed
      let speedLerpAlpha = 0
      const lerpDuration = 60

      const interval = setInterval(() => {
        const baseSpeed = loading ? 0.05 : 0.02
        const randomSpeed = Math.random() * baseSpeed * 2 - baseSpeed
        targetSpeed = randomSpeed
        speedLerpAlpha = 0
      }, 5000)

      const animate = () => {
        requestAnimationFrame(animate)

        if (speedLerpAlpha < 1) {
          speedLerpAlpha += 1 / lerpDuration
          currentSpeed = THREE.MathUtils.lerp(currentSpeed, targetSpeed, speedLerpAlpha)
        }

        if (sphere) sphere.rotation.y += currentSpeed

        controls.update()
        renderer.render(scene, camera)
      }
      animate()

      // Clear interval on unmount
      return () => clearInterval(interval)
    })

    // Full cleanup on unmount or re-run
    return () => {
      renderer.domElement.removeEventListener("click", handleClick)
      renderer.domElement.removeEventListener("mousemove", handleMouseMove)
      if (current && renderer.domElement) {
        current.removeChild(renderer.domElement)
      }
      renderer.dispose()
      if (sphere) scene.remove(sphere)
      if (material) material.dispose()
      if (geometry) geometry.dispose()
      if (loadedTexture) loadedTexture.dispose()
    }
  }, [loading, onClick, image, size, times]) // re-run only when these change

  return <div ref={mountRef} />
}
